/**
 * next-pulse-progress — cooldown indicator showing the next-Pulse date and a
 * continuous progress bar indicating time elapsed through the cooldown toward
 * the next Pulse.
 *
 * Usage contexts:
 *  - `pulse-page` (inside `next-brainhealth-checkin` container): showDate=true
 *    (default). Shows calendar icon + short formatted date + progress bar + day label.
 *  - `task-list-item` (Dashboard card cooldown state): showDate=false. Shows
 *    only the progress bar + day label — the card's own layout handles context.
 */
import React from "react";
import styled from "styled-components";
import { format } from "date-fns";

import { StyledP } from "components/simple";
import { color, fontFamily, fontSize, spacingDefaults } from "style/constants";
import { ReactComponent as CalendarSvg } from "assets/calendar_today.svg";

export type NextPulseProgressProps = {
  /** The date the next Pulse becomes available (ISO string) */
  nextPulseDate: string;
  /** Current day in the cooldown (1-based, e.g., 1 through 7) */
  currentDay: number;
  /** Total days in cooldown period (typically 7) */
  totalDays: number;
  /** Whether to display the next-Pulse date. Default: true.
   *  Set to false when rendered inside task-list-item (Program Bar only). */
  showDate?: boolean;
  /** Whether to hide the card background. Default: false. */
  transparent?: boolean;
};

interface CardWrapperProps {
  transparent?: boolean;
}

const CardWrapper = styled(
  ({
    transparent,
    ...props
  }: CardWrapperProps & React.HTMLAttributes<HTMLDivElement>) => (
    <div {...props} />
  )
)<CardWrapperProps>`
  background-color: ${props =>
    props.transparent ? "transparent" : color.SOFTBLUE};
  border-radius: 8px;
  padding: ${props => (props.transparent ? "0" : spacingDefaults.normal)};
  width: 100%;
`;

const DateRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacingDefaults.xsmall};
  margin-bottom: ${spacingDefaults.xsmall};
  height: 21px;
`;

const ProgramBar = styled.div`
  position: relative;
  width: 100%;
  height: 25px;
`;

const BarTrack = styled.div`
  position: absolute;
  top: 12%;
  bottom: 24%;
  left: 0;
  right: 0;
  border-radius: 64px;
  background-color: ${color.BLUEGRAY};
`;

interface BarFillProps {
  percentage: number;
}

// Fill anchored to the right (depleting). Uses same pill radius.
const BarFill = styled.div<BarFillProps>`
  position: absolute;
  left: 0;
  top: 0;
  width: ${props => props.percentage}%;
  height: 100%;
  border-radius: 64px;
  background-color: ${color.PULSE_PROGRESS};
  transition: width 0.3s ease;
`;

interface DayLabelProps {
  onFill: boolean;
}

// Day label centered within the bar track.
// Destructure onFill to prevent it from being forwarded to the DOM span.
const DayLabel = styled(
  ({
    onFill,
    ...props
  }: DayLabelProps & React.HTMLAttributes<HTMLSpanElement>) => (
    <span {...props} />
  )
)<DayLabelProps>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-family: ${fontFamily.primary};
  font-size: ${fontSize.xsmall};
  color: ${props => (props.onFill ? color.WHITE : color.BLUE)};
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  z-index: 1;
`;

const NextPulseProgress: React.FC<NextPulseProgressProps> = ({
  nextPulseDate,
  currentDay,
  totalDays,
  showDate = true,
  transparent = false
}) => {
  const formattedDate = nextPulseDate
    ? format(new Date(nextPulseDate), "MMM d")
    : "";
  const remainingDays = totalDays - currentDay;
  const percentage =
    totalDays > 0 ? Math.max(100 - (remainingDays / totalDays) * 100, 0) : 0;
  const dayText = remainingDays === 1 ? "1 Day" : `${remainingDays} Days`;

  return (
    <CardWrapper data-testid="next-pulse-progress" transparent={transparent}>
      {showDate && (
        <DateRow data-testid="next-pulse-date">
          <CalendarSvg width="15" height="15" aria-hidden="true" />
          <StyledP
            color={color.DARKBLUE}
            fontFamily={fontFamily.primary}
            fontSize={fontSize.small}
            data-testid="next-pulse-date-text"
          >
            {formattedDate}
          </StyledP>
        </DateRow>
      )}

      <ProgramBar>
        <BarTrack
          role="progressbar"
          aria-valuenow={currentDay}
          aria-valuemin={1}
          aria-valuemax={totalDays}
          aria-label={`Pulse cooldown progress, day ${currentDay} of ${totalDays}`}
          data-testid="next-pulse-progress-bar"
        >
          <BarFill
            percentage={percentage}
            data-testid="next-pulse-progress-fill"
          />
          <DayLabel onFill={percentage > 50} data-testid="next-pulse-day-label">
            {dayText}
          </DayLabel>
        </BarTrack>
      </ProgramBar>
    </CardWrapper>
  );
};

export default NextPulseProgress;
